/**
 * SEO-friendly listing URLs.
 *
 *   /vehicles/142  ->  /vehicles/toyota-innova-crysta-2019-142
 *
 * The numeric id stays on the END of the slug and is the only part that is
 * looked up. Nothing is stored: the slug is derived from brand/model/year on
 * the way out and thrown away on the way in, so renaming a listing can never
 * orphan a URL and there is no uniqueness constraint to police.
 *
 * Keeping the id last is what makes parsing unambiguous — models legitimately
 * contain digits ("Xcent 1.2", "Eeco 7 Seater"), so only a trailing group can
 * be trusted to be the id.
 *
 * MIRROR: backend/utils/vehicleSlug.js is the same logic for the sitemap,
 * IndexNow pings and dealer share links. The two must not drift — a slug that
 * differs between them would put a canonical on the page that points at a URL
 * the sitemap never lists.
 *
 * scripts/inject-seo-heads.js loads THIS file at build time (it strips the
 * `export` keywords and requires it), so keep every declaration a plain
 * top-level `export function` and keep the file free of imports.
 */

/** Lowercase ASCII, hyphen-separated, no leading/trailing or repeated hyphens. */
export function slugify(value) {
  return String(value)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function present(value) {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

/**
 * `toyota-innova-crysta-2019-142`, or bare `142` when a listing has no
 * brand/model/year worth slugifying (the URL still resolves either way).
 */
export function vehicleSlug(vehicle) {
  if (!vehicle || !present(vehicle.id)) return '';
  const id = String(vehicle.id).trim();
  const words = [vehicle.brand, vehicle.model, vehicle.year]
    .filter(present)
    .map(slugify)
    .filter(Boolean)
    .join('-');
  return words ? `${words}-${id}` : id;
}

/** Path form, for <Link to> and canonical URLs. */
export function vehiclePath(vehicle) {
  const slug = vehicleSlug(vehicle);
  return slug ? `/vehicles/${slug}` : '/vehicles';
}

/**
 * Pull the id back out of either URL form — `toyota-innova-crysta-2019-<uuid>`,
 * a bare `<uuid>`, or the legacy numeric `142` / `toyota-innova-crysta-2019-142`.
 *
 * A UUID is matched FIRST and as a whole. The original `/(\d+)$/` assumed ids
 * were numeric, which they have never been in this schema — `vehicles.id` is a
 * UUID. On a UUID that regex returns either null (ends in a letter) or a
 * meaningless trailing fragment like `071648` (ends in a digit), and
 * VehicleDetail then bounces the visitor to /vehicles. Every listing URL on the
 * site was a dead end because of it.
 *
 * The numeric branch is kept for any legacy link that really does end in a bare
 * number, and is only reached when no trailing UUID is present.
 */
export function parseVehicleId(slug) {
  const value = String(slug == null ? '' : slug).trim();
  const uuid = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i.exec(value);
  if (uuid) return uuid[1];
  const numeric = /(\d+)$/.exec(value);
  return numeric ? numeric[1] : null;
}
